import React, { CSSProperties, ReactNode } from "react"

import { TiltTracking, useTilt } from "../../hooks"

import "./style.scss"

export interface Tilt3DProps {
  children: ReactNode
  /** Zone de suivi de la souris : l'élément lui-même ou toute la fenêtre */
  tracking?: TiltTracking
  /** Inclinaison maximale en degrés */
  maxTilt?: number
  /** Reflet lumineux qui suit la souris */
  glare?: boolean
  /** Éclat qui balaie l'élément à intervalle régulier */
  shine?: boolean
  /** Légère lévitation continue */
  float?: boolean
  /** Arrondi du reflet, à aligner sur celui du contenu */
  borderRadius?: string
  className?: string
}

/**
 * Affiche son contenu en 3D : il s'incline selon la position de la souris,
 * avec des reflets optionnels. Le contenu reste cliquable et focusable.
 * Les effets sont désactivés si l'utilisateur préfère réduire les animations.
 * Utiliser `Tilt3DLayer` pour décoller un élément en profondeur.
 */
const Tilt3D = ({
  children,
  tracking = "element",
  maxTilt,
  glare = true,
  shine = false,
  float = false,
  borderRadius = "0",
  className,
}: Tilt3DProps) => {
  const tiltRef = useTilt<HTMLDivElement>({ tracking, maxTilt })
  const classNames = [
    "clux-tilt-3d",
    float && "is-floating",
    shine && "is-shining",
    className,
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div
      ref={tiltRef}
      className={classNames}
      data-testid="clux-tilt-3d"
      style={{ "--clux-tilt-radius": borderRadius } as CSSProperties}
    >
      <div className="clux-tilt-3d__inner">
        {children}
        {(glare || shine) && (
          <span
            className={`clux-tilt-3d__glare${glare ? " has-glare" : ""}`}
            aria-hidden
            data-testid="clux-tilt-3d-glare"
          />
        )}
      </div>
    </div>
  )
}

export default Tilt3D
